/**
 * API utility functions for the Get Talky Agent
 * Uses proxy server for Google Identity Token authentication and CORS handling
 */

import { handleAuthError } from './auth';

// Use relative URL for production, fallback to localhost for local development
const getApiBaseUrl = () => {
  // In production, use relative URLs (same origin)
  if (process.env.NODE_ENV === 'production') {
    return '/api/proxy';
  }
  
  // For local development, check for custom API base URL or use localhost
  return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/proxy';
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Get the client access token (replaces hardcoded CLIENT_ID)
 * Uses server-validated access token or local development token
 */
const getAccessToken = () => {
  // Get access token that was validated by server or set in local development
  const validatedToken = window.VALIDATED_ACCESS_TOKEN;
  
  if (!validatedToken) {
    console.error('No validated access token available');
    throw new Error('Authentication required - no valid access token');
  }
  
  return validatedToken;
};

/**
 * Makes authenticated API requests through the proxy server
 * The proxy server handles Google Identity Token authentication automatically
 * @param {string} endpoint - The API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} The API response
 */
const makeAuthenticatedRequest = async (endpoint, options = {}) => {
  try {
    // Get dynamic access token (replaces hardcoded CLIENT_ID)
    const ACCESS_TOKEN = getAccessToken();
    
    // The proxy server handles authentication, so we just need to route through it
    const url = `${API_BASE_URL}${endpoint}${endpoint.includes('?') ? '&' : '?'}client_id=${ACCESS_TOKEN}`;
    
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    };

    console.log(`Making API request to: ${url} (token: ${ACCESS_TOKEN.substring(0, 8)}...)`);

    const response = await fetch(url, {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || errorData.detail || `HTTP error! status: ${response.status}`;
      
      // Handle authentication errors specially
      if (response.status === 401 || response.status === 403) {
        const authError = handleAuthError(new Error(errorMessage));
        console.error('Authentication error:', authError);
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    // Handle network and other errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      const networkError = handleAuthError(error);
      console.error('Network error:', networkError);
    }
    
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};

/**
 * Fetch interaction types from the API
 * @returns {Promise<Object>} The interaction types configuration
 */
export const fetchInteractionTypes = async () => {
  try {
    const data = await makeAuthenticatedRequest('/interaction-types');
    return data;
  } catch (error) {
    console.error('Failed to fetch interaction types:', error);
    
    // Return fallback data if API fails
    return {
      success: true,
      interactionTypes: [
        { 
          value: 'appointment', 
          label: 'Appointments', 
          urgency: 4,
          styling: { pill: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
          isFilter: true 
        },
        { 
          value: 'insurance', 
          label: 'Insurance', 
          urgency: 5,
          styling: { pill: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
          isFilter: true 
        },
        { 
          value: 'billing', 
          label: 'Billing', 
          urgency: 5,
          styling: { pill: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
          isFilter: true 
        },
        { 
          value: 'faq', 
          label: 'FAQ', 
          urgency: 6,
          styling: { pill: 'bg-gray-100 text-gray-700', dot: 'bg-gray-500' },
          isFilter: true 
        },
      ]
    };
  }
};

/**
 * Fetch interactions from the API
 * @returns {Promise<Object>} The interactions data
 */
export const fetchInteractions = async () => {
  try {
    const data = await makeAuthenticatedRequest('/interactions');
    return data;
  } catch (error) {
    console.error('Failed to fetch interactions:', error);
    throw error;
  }
};

/**
 * Update interaction status
 * @param {string|number} interactionId - The interaction ID
 * @param {string} status - The new status
 * @returns {Promise<Object>} The updated interaction
 */
export const updateInteractionStatus = async (interactionId, status) => {
  try {
    const endpoint = `/interactions/${interactionId}/status?status=${encodeURIComponent(status)}`;
    const data = await makeAuthenticatedRequest(endpoint, {
      method: 'PUT',
    });
    return data;
  } catch (error) {
    console.error('Failed to update interaction status:', error);
    throw error;
  }
};

/**
 * Update interaction unread status
 * @param {string|number} interactionId - The interaction ID
 * @param {boolean} unread - The unread status
 * @returns {Promise<Object>} The updated interaction
 */
export const updateInteractionUnreadStatus = async (interactionId, unread) => {
  try {
    const endpoint = `/interactions/${interactionId}/unread?unread=${unread}`;
    const data = await makeAuthenticatedRequest(endpoint, {
      method: 'PUT',
    });
    return data;
  } catch (error) {
    console.error('Failed to update interaction unread status:', error);
    throw error;
  }
};

/**
 * Update interaction details
 * @param {string|number} interactionId - The interaction ID
 * @param {Object} details - The details to update
 * @param {Object} topLevelFields - Top-level fields to update
 * @returns {Promise<Object>} The updated interaction
 */
export const updateInteractionDetails = async (interactionId, details, topLevelFields) => {
  try {
    const data = await makeAuthenticatedRequest(`/interactions/${interactionId}/details`, {
      method: 'PUT',
      body: JSON.stringify({ details, topLevelFields }),
    });
    return data;
  } catch (error) {
    console.error('Failed to update interaction details:', error);
    throw error;
  }
};

/**
 * Create a new appointment
 * @param {Object} appointmentData - The appointment data
 * @returns {Promise<Object>} The created appointment
 */
export const createAppointment = async (appointmentData) => {
  try {
    const data = await makeAuthenticatedRequest('/appointments', {
      method: 'POST',
      body: JSON.stringify(appointmentData),
    });
    return data;
  } catch (error) {
    console.error('Failed to create appointment:', error);
    throw error;
  }
};

/**
 * Get appointment types from the API
 * @returns {Promise<Object>} The appointment types data
 */
export const fetchAppointmentTypes = async () => {
  try {
    const data = await makeAuthenticatedRequest('/appointment-types');
    return data;
  } catch (error) {
    console.error('Failed to fetch appointment types:', error);
    throw error;
  }
};

/**
 * Get dashboard statistics
 * @returns {Promise<Object>} The dashboard statistics
 */
export const fetchDashboardStats = async () => {
  try {
    const data = await makeAuthenticatedRequest('/stats');
    return data;
  } catch (error) {
    console.error('Failed to fetch dashboard stats:', error);
    throw error;
  }
}; 